import { DocumentEnquiryConstant } from "presentation/constant/DocumentEnquiry/DocumentEnquiryConstant";
import { useDocumentEnquiryVM } from "presentation/hook/DocumentEnquiry/useDocumentEnquiryVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useDocumentEnquiryTracked } from "presentation/store/DocumentEnquiry/DocumentEnquiryProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useEffect, useMemo } from "react";
import { DialogModal, FieldType, HPHButton, HPHCheckbox, IconButton, IFieldValue } from "veronica-ui-component/dist/component/core";
import CustomSettingPanel from "../Accordion/CustomSettingPanel";

const DocumentPrintModal: React.FC = () => {
    const [docEnquiryState] = useDocumentEnquiryTracked();
    const docEnquiryVM = useDocumentEnquiryVM();
    const messageBarVM = useMessageBarVM();

    const { selectedRows, isShowPrintPad, docPrintCriteria, dynamicOptions, allFormState } = docEnquiryState;

    const MODAL_CONST = DocumentEnquiryConstant.Modal;

    const { printQueueDropdownOptions, fileLocationDropdownOptions } = dynamicOptions;


    useEffect(() => {
        isShowPrintPad && docEnquiryVM.loadModalDropdownOption();
    }, [docEnquiryVM, isShowPrintPad])

    const onPrintClicked = async () => {
        let isMissing = false;
        await docEnquiryVM.onReprintClick(docPrintCriteria).then((data) => {
            if (data && Object.keys(data).length === 0) {
                isMissing = true;
            }
        });
        if (isMissing) {
            return;
        }
        docEnquiryVM.showLoading();
        await docEnquiryVM.onReprint(selectedRows, docPrintCriteria).then((data) => {
            if (data && data["printReportFail"]) {
                messageBarVM.showError(data["printReportFail"], true);
                return;
            }
            messageBarVM.showSuccess("Print/Send Successfully.", true);
        }).finally(() => {
            docEnquiryVM.hideLoading();
        })
    }

    const memoWithSupp = useMemo(() =>
        <>
            <div className='im-flex-row-item'>
                <HPHCheckbox
                    label={"With Supporting Document"}
                    selectedValues={docPrintCriteria.printWithSupp}
                    onChange={(e: any) => docEnquiryVM.onModalCheckboxChange(e.checked, "printWithSupp")}
                />
            </div>
        </>
        , [docEnquiryVM, docPrintCriteria.printWithSupp])

    const memoInvPrinter = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "150px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={docPrintCriteria.invPrinter || ''}
                fieldValue={docPrintCriteria.invPrinter}
                fieldLabel={"Printer"}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'invPrinter'}
                maxLength={20}
                options={printQueueDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    docEnquiryVM.onModalDropdownChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, docEnquiryVM, docPrintCriteria.invPrinter, printQueueDropdownOptions])

    const memoInvFileLoc = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "150px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={docPrintCriteria.invFileLoc || ''}
                fieldValue={docPrintCriteria.invFileLoc}
                fieldLabel={"File Location"}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'invFileLoc'}
                maxLength={30}
                options={fileLocationDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    docEnquiryVM.onModalDropdownChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [docEnquiryVM, docPrintCriteria.invFileLoc, fileLocationDropdownOptions])

    const memoSuppDocPrinter = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "150px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={docPrintCriteria.suppPrinter || ''}
                fieldValue={docPrintCriteria.suppPrinter}
                fieldLabel={"Printer"}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'suppPrinter'}
                maxLength={20}
                options={printQueueDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    docEnquiryVM.onModalDropdownChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, docEnquiryVM, docPrintCriteria.suppPrinter, printQueueDropdownOptions])

    const memoSuppDocFileLoc = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "150px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={docPrintCriteria.suppFileLoc || ''}
                fieldValue={docPrintCriteria.suppFileLoc}
                fieldLabel={"File Location"}
                isSaveClicked={true}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'suppFileLoc'}
                maxLength={20}
                options={fileLocationDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    docEnquiryVM.onModalDropdownChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [docEnquiryVM, docPrintCriteria.suppFileLoc, fileLocationDropdownOptions])


    return <DialogModal style={{ minWidth: '650px', minHeight: '650px' }}
        appendTo='self'
        showButton={false}
        visible={isShowPrintPad}
        positions="center"
        resizable={false}
        header={
            <div className="im-charge-data-search-confirm-modal-header">
                <span className="name">DOCUMENTS TO PRINT</span>
                <IconButton fileName='Icon-cross' size="medium" onClick={docEnquiryVM.closeConfirmModal} tooltipDisable={true} />
            </div>
        }
        dialogContent={
            <div className="im-charge-data-search-confirm-modal-content">

                <CriteriaItemContainer>
                    {memoWithSupp}
                </CriteriaItemContainer>

                <CriteriaItemContainer>
                    <span className="DocPrintLabel">{MODAL_CONST.PRINTER}</span>
                </CriteriaItemContainer>

                <CriteriaItemContainer>
                    <span className="DocPrintLabel">{MODAL_CONST.PRINTER_INV}</span>
                    {memoInvPrinter}{memoInvFileLoc}
                </CriteriaItemContainer>

                <CriteriaItemContainer>
                    <span className="DocPrintLabel">{MODAL_CONST.PRINTER_SUPP}</span>
                    {memoSuppDocPrinter}{memoSuppDocFileLoc}
                </CriteriaItemContainer>

                <CriteriaItemContainer>
                    <CustomSettingPanel docEnquiryState={docEnquiryState} />
                </CriteriaItemContainer>

            </div>
        }
        className="im-charge-data-search-confirm-modal"
        footer={
            <div className="im-charge-data-search-confirm-modal-footer">
                <HPHButton label={'Print'} size={'Small'} theme={'Primary'} onClick={onPrintClicked} />
            </div>
        }
    />;
};

export default memo(DocumentPrintModal);